import * as _ from 'lodash';
import { PageBase, PageOptionsBase } from '../../Common/PageBase';
import { DatatableApi, Datatable, DatatableOptions } from '../../Widgets/Datatable'
import { MultiSelectApi } from '../../Widgets/MultiSelect'
import { DateTimePickerApi } from '../../Widgets/DateTimePicker';
import { Utils } from '../../Utils/Utils';
import { DateUtils } from '../../Utils/DateUtils';
import { StringUtils } from '../../Utils/StringUtils';
import { ArrayUtils } from '../../Utils/ArrayUtils';
import { StatusMessage, StatusMessageModel, StatusMessageType } from '../../Widgets/StatusMessage'
import { StatusMessageHelper } from '../../Utils/StatusMessageHelper';

export interface IndexPageOptions extends PageOptionsBase {
    urlList: string,
    urlCount: string
}

var FILTER_DELIM = '-delim-';
const batchSize = 100;

class RequestLogBrowseRequest {
    public Id: string;
    public PlayerId: string;
    public PlayerSessionId: string;
    public GameRoundId: string;
    public Game: string;
    public Customer: string;
    public CustomerOperation: string;
    public Source: string;
    public TimestampFrom: number | null;
    public TimestampTo: number | null;
    public DurationFrom: string;
    public DurationTo: string;
    public Result: string;
    public IpAddress: string;
    public Hostname: string;
    public Status: string;
    public RequestReference: string;
    public Action: string;
    public ErrorId: string;

    public LastId: number | null;
    public BatchSize: number;
}

export class IndexPage {
    private _options: IndexPageOptions;
    private _runButton: JQuery;
    private _loadMoreButton: JQuery;
    private _resetFilterButton: JQuery;
    private _currentRequest: RequestLogBrowseRequest;

    private _filterId: JQuery;
    private _filterPlayerId: JQuery;
    private _filterPlayerSessionId: JQuery;
    private _filterGameRoundId: JQuery;
    private _filterGame: MultiSelectApi;
    private _filterCustomer: MultiSelectApi;
    private _filterCustomerOperation: MultiSelectApi;
    private _filterCreatedFrom: DateTimePickerApi;
    private _filterCreatedTo: DateTimePickerApi;
    private _filterDurationFrom: JQuery;
    private _filterDurationTo: JQuery;
    private _filterIpAddress: JQuery;
    private _filterHostname: JQuery;
    private _filterSource: MultiSelectApi;
    private _filterStatus: JQuery;
    private _filterRequestReference: JQuery;
    private _filterAction: JQuery;
    private _filterErrorId: JQuery;
    private _filterResult: MultiSelectApi;

    private _btnCountResult: JQuery;
    private _totalEntries: JQuery;
    private _processing: JQuery;

    constructor(options: IndexPageOptions) {
        this._options = options;

        this.run = this.run.bind(this);
        this.showLoadMoreButton = this.showLoadMoreButton.bind(this);
        this.hideLoadMoreButton = this.hideLoadMoreButton.bind(this);
        this.enableRunButton = this.enableRunButton.bind(this);
        this.loadMore = this.loadMore.bind(this);
        this.resetFilter = this.resetFilter.bind(this);
        this.renderDetailRow = this.renderDetailRow.bind(this);
        this.renderContextData = this.renderContextData.bind(this);
        this.getCountResult = this.getCountResult.bind(this);
        this.toggleDetail = this.toggleDetail.bind(this);

        this._runButton = Utils.getJQueryById("btnRunQuery");
        this._runButton.on("click", this.run);
        this._loadMoreButton = Utils.getJQueryById("btnLoadMore");
        this._loadMoreButton.on("click", this.loadMore);
        this._resetFilterButton = Utils.getJQueryById("btnResetFilter");
        this._resetFilterButton.on("click", this.resetFilter);
        this._btnCountResult = Utils.getJQueryById("btnCountResult");
        this._btnCountResult.on("click", this.getCountResult);
        this._totalEntries = Utils.getJQueryById("totalEntries");
        this._processing = Utils.getJQueryById("processing");

        this._filterId = Utils.getJQueryById('filterId');
        this._filterPlayerId = Utils.getJQueryById('filterPlayer');
        this._filterPlayerSessionId = Utils.getJQueryById('filterPlayerSession');
        this._filterGameRoundId = Utils.getJQueryById('filterGameRound');
        this._filterGame = new MultiSelectApi('filterGame');
        this._filterCustomer = new MultiSelectApi('filterCustomer');
        this._filterCustomerOperation = new MultiSelectApi('filterCustomerOperation');
        this._filterCreatedFrom = new DateTimePickerApi('filterCreatedFrom');
        this._filterCreatedTo = new DateTimePickerApi('filterCreatedTo');
        this._filterDurationFrom = Utils.getJQueryById('filterDurationFrom');
        this._filterDurationTo = Utils.getJQueryById('filterDurationTo');
        this._filterIpAddress = Utils.getJQueryById('filterIpAddress');
        this._filterHostname = Utils.getJQueryById('filterHostName');
        this._filterSource = new MultiSelectApi('filterSource');
        this._filterStatus = Utils.getJQueryById('filterStatus');
        this._filterRequestReference = Utils.getJQueryById('filterRequestReference');
        this._filterAction = Utils.getJQueryById('filterAction');
        this._filterErrorId = Utils.getJQueryById('filterErrorId');
        this._filterResult = new MultiSelectApi('filterResult');
    }

    private run(): void {
        this.disableRunButton();
        let totalEntries = <HTMLSpanElement>this._totalEntries[0];
        totalEntries.innerText = "##";
        this._currentRequest = this.getBrowseRequest();
        let table = this.getTableBody();
        table.innerHTML = "";
        this.sendRequest(this._currentRequest);
    }

    private getTableBody(): HTMLTableSectionElement {
        return document.getElementById("tableRequestLogs").getElementsByTagName("tbody")[0];
    }

    private getBrowseRequest(): RequestLogBrowseRequest {
        let request = new RequestLogBrowseRequest();
        request.Id = this._filterId.val();
        request.PlayerId = this._filterPlayerId.val();
        request.PlayerSessionId = this._filterPlayerSessionId.val();
        request.GameRoundId = this._filterGameRoundId.val();
        request.Game = this._filterGame.isAllSelected() ? '' : this._filterGame.getSelectionValues(FILTER_DELIM);
        request.Customer = this._filterCustomer.isAllSelected() ? '' : this._filterCustomer.getSelectionValues(FILTER_DELIM);
        request.CustomerOperation = this._filterCustomerOperation.isAllSelected() ? '' : this._filterCustomerOperation.getSelectionValues(FILTER_DELIM);
        request.TimestampFrom = this._filterCreatedFrom.getUnixTimestamp();
        request.TimestampTo = this._filterCreatedTo.getUnixTimestamp();
        request.DurationFrom = this._filterDurationFrom.val();
        request.DurationTo = this._filterDurationTo.val();
        request.IpAddress = this._filterIpAddress.val();
        request.Hostname = this._filterHostname.val();
        request.Source = this._filterSource.isAllSelected() ? '' : this._filterSource.getSelectionValues(FILTER_DELIM);
        request.Status = this._filterStatus.val();
        request.RequestReference = this._filterRequestReference.val();
        request.Action = this._filterAction.val();
        request.ErrorId = this._filterErrorId.val();
        request.Result = this._filterResult.isAllSelected() ? '' : this._filterResult.getSelectionValues(FILTER_DELIM);
        request.BatchSize = batchSize + 1;
        return request;
    }

    private sendRequest(request: RequestLogBrowseRequest) {
        let urlList = this._options.urlList;
        let table = this.getTableBody();
        let showLoadMore = this.showLoadMoreButton;
        let hideLoadMore = this.hideLoadMoreButton;
        let enableRunButton = this.enableRunButton;
        let processing = this._processing;
        let displayMessage = this.displayMessage;
        let renderDetailRow = this.renderDetailRow;
        let toggleDetail = this.toggleDetail;
        var jsonData = JSON.stringify(request);
        processing.show();
        $.ajax({
            url: urlList,
            type: "POST",
            dataType: 'json',
            contentType: "application/json",
            data: jsonData,
            success: function (data) {
                let items = data;
                if (items.length < batchSize + 1) {
                    hideLoadMore();
                }
                else {
                    request.LastId = items[batchSize].Id;
                    showLoadMore();
                }
                $.each(items, function (index, item) {
                    if (index < batchSize) {
                        let row = table.insertRow(-1);
                        row.id = `requestLog${item.Id}`;
                        row.className = "cursor-pointer";
                        row.onclick = () => toggleDetail(row.id);
                        let cell0 = row.insertCell(0);
                        let errorId = Utils.isNullOrUndefined(item.ErrorId) ? '' : item.ErrorId;
                        cell0.innerHTML = `${item.Id}<br /> <a id='lnkName${errorId}' href='Logs/Detail/${errorId}'>${errorId}</a>`;
                        cell0.className = `log-level-${item.Result.toLowerCase()}`;
                        let cell1 = row.insertCell(1);
                        cell1.innerHTML = `<b>Player: </b>${item.PlayerId}<br /><b>Session: </b>${item.PlayerSessionId}`;
                        let cell2 = row.insertCell(2);
                        cell2.innerHTML = `<b>Customer: </b>${item.CustomerCode}<br /><b>Operation: </b>${item.OperationCode}`;
                        let cell3 = row.insertCell(3);
                        let gameRoundId = Utils.isNullOrUndefined(item.GameRoundId) || item.GameRoundId === 0 ? '' : item.GameRoundId;
                        cell3.innerHTML = `<b>Game: </b>${item.GameCode}<br /><b>Round ID: </b>${gameRoundId}`;
                        let cell4 = row.insertCell(4);
                        cell4.innerHTML = `${item.Created}`;
                        let cell5 = row.insertCell(5);
                        cell5.innerHTML = `${item.Duration}`;
                        let cell6 = row.insertCell(6);
                        cell6.innerHTML = `${item.IpAddress}`;
                        let cell7 = row.insertCell(7);
                        cell7.innerHTML = `<b>Status: </b>${item.Status}<br /><b>Result: </b>${item.Result}`;
                        let cell8 = row.insertCell(8);
                        cell8.innerHTML = `${item.HostName}`;
                        let cell9 = row.insertCell(9);
                        cell9.innerHTML = `${item.Source}`;
                        let cell10 = row.insertCell(10);
                        cell10.innerHTML = `${item.Action}`;

                        let detailRow = table.insertRow(-1);
                        detailRow.setAttribute("style", "display: none");
                        detailRow.id = `requestLog${item.Id}detail`;
                        let detailCell = detailRow.insertCell(0);
                        detailCell.colSpan = 11;
                        detailCell.innerHTML = renderDetailRow(item);
                    }
                });
                processing.hide();
                enableRunButton();
            },
            error: function (jqXHR, textStatus, errorThrown) {
                processing.hide();
                enableRunButton();
                displayMessage(jqXHR);
            }
        });
    }

    private showLoadMoreButton(): void {
        this._loadMoreButton.removeClass("display-none");
    }

    private hideLoadMoreButton(): void {
        this._loadMoreButton.addClass("display-none");
    }

    private loadMore(): void {
        this.sendRequest(this._currentRequest);
    }

    private disableRunButton(): void {
        this._runButton.attr("disabled", 1);
    }

    private enableRunButton(): void {
        this._runButton.removeAttr("disabled");
    }

    private resetFilter(): void {
        this._filterId.val('');
        this._filterPlayerId.val('');
        this._filterPlayerSessionId.val('');
        this._filterGameRoundId.val('');
        this._filterGame.selectAll();
        this._filterCustomer.selectAll();
        this._filterCustomerOperation.selectAll();
        var createdFrom = DateUtils.getUtcMoment().subtract(7, 'days').set({ hours: 0, minutes: 0, seconds: 0 });
        this._filterCreatedFrom.setUTCDate(createdFrom);
        var createdTo = DateUtils.getUtcMoment().endOf('day');
        this._filterCreatedTo.setUTCDate(createdTo);
        this._filterDurationFrom.val('');
        this._filterDurationTo.val('');
        this._filterIpAddress.val('');
        this._filterHostname.val('');
        this._filterSource.selectAll();
        this._filterStatus.val('');
        this._filterRequestReference.val('');
        this._filterAction.val('');
        this._filterErrorId.val('');
        this._filterResult.selectAll();
    }

    private displayMessage(jqXHR: JQueryXHR): void {
        var statusMessageWrapper = Utils.getJQueryById("statusMessageWrapper");
        let statusMessageData = <StatusMessageModel>{
            code: jqXHR.status.toString(),
            autoClose: false,
            class: "danger",
            message: jqXHR.statusText,
            type: StatusMessageType.Error
        };
        var statusMessage = new StatusMessage(statusMessageData);
        statusMessageWrapper.html(statusMessage.getHTMLmessage());
        StatusMessageHelper.initValidator();
        StatusMessageHelper.initSatusMessage();
    }

    private toggleDetail(id: string) {
        let detailId = `${id}detail`;
        let detailRow = document.getElementById(detailId);
        if (detailRow.hasAttribute("style")) {
            detailRow.removeAttribute("style");
        }
        else {
            detailRow.setAttribute("style", "display: none");
        }
    }

    private renderDetailRow(data: any): string {
        var css = 'info';
        if (data.Result === 'Warning') {
            css = 'warning';
        }
        if (data.Result === 'Error') {
            css = 'danger';
        }

        var result = `<div class='note note-${css}'>`;

        if (!StringUtils.isNullOrEmpty(data.ContextData)) {
            result += `<h5>Context</h5>
                    <p>${this.renderContextData(data.ContextData)}</p>
                    <p>&nbsp;</p>`;
        }

        if (!StringUtils.isNullOrEmpty(data.ExceptionData)) {
            result += `<h5>Exception</h5>
                    <p class="errorLogContextData">${_.escape(data.ExceptionData)}</p>
                    <p>&nbsp;</p>`;
        }

        if (!StringUtils.isNullOrEmpty(data.RequestData)) {
            result += `<h5>Request</h5>
                    <p class="errorLogContextData">${_.escape(data.RequestData)}</p>
                    <p>&nbsp;</p>`;
        }

        if (!StringUtils.isNullOrEmpty(data.ResponseData)) {
            result += `<h5>Response</h5>
                    <p class="errorLogContextData">${_.escape(data.ResponseData)}</p>
                    <p>&nbsp;</p>`;
        }

        if (!StringUtils.isNullOrEmpty(data.RequestReference)) {
            result += `<h5>Request reference</h5>
                    <p class="errorLogContextData">${_.escape(data.RequestReference)}</p>
                    <p>&nbsp;</p>`;
        }

        result += '</div>';

        return result;
    }

    private renderContextData(contextData: string) {
        var json: Object = null;
        try {
            json = JSON.parse(contextData);
        } catch (e) {
            json = null;
        }

        var result = '';
        if (json === null || !_.isArray(json)) {
            result = contextData;
        } else {
            result = '<table class="errorLogContextData table-bordered table-hover">';
            _.each(json, item => {
                for (var key in item) {
                    result += `<tr><td>${key}</td><td>${StringUtils.escapeHtml(item[key])}</td></tr>`;
                }
            });

            result += '</table>';
        }

        return result;
    }

    private getCountResult() {
        let request = Utils.isNullOrUndefined(this._currentRequest) ? this.getBrowseRequest() : this._currentRequest;
        let urlCount = this._options.urlCount;
        let totalEntries = <HTMLSpanElement>this._totalEntries[0];
        let processing = this._processing;
        let displayMessage = this.displayMessage;
        var jsonData = JSON.stringify(request);
        processing.show();
        $.ajax({
            url: urlCount,
            type: "POST",
            dataType: 'json',
            contentType: "application/json",
            data: jsonData,
            success: function (data) {
                totalEntries.innerText = data;
                processing.hide();
            },
            error: function (jqXHR, textStatus, errorThrown) {
                processing.hide();
                displayMessage(jqXHR);
            }
        });
    }
}


/** WEBPACK FOOTER **
 ** ./Typescript/Pages/RequestLog/IndexPage.ts
 **/